import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  BarChart3, 
  Phone,
  ClipboardList,
  CreditCard,
  Settings, 
  Bell, 
  MoreHorizontal,
  LogOut,
  Menu,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const AdminLayout = ({ children, user, onLogout }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Users', path: '/users' },
    { icon: CreditCard, label: 'Payout Requests', path: '/payout-requests' },
    { icon: FileText, label: 'Sales Reports', path: '/sales-report' },
    { icon: BarChart3, label: 'Service Reports', path: '/service-report' },
    { icon: Phone, label: 'Call Report', path: '/call-report' },
    { icon: Bell, label: 'Notifications', path: '/notifications' },
    { icon: Settings, label: 'Settings', path: '/settings' },
    { icon: ClipboardList, label: 'WFH Employee Report', path: '/wfh-employee-report' },
    { icon: MoreHorizontal, label: 'More Options', path: '/more' },
  ];

  const isActive = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    // highlight parent for nested routes like /complaints/:id
    return location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="ml-3 text-xl font-bold text-gray-900">RoadServe Admin</h1>
        </div>
        <button
          onClick={onLogout}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <LogOut size={20} />
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
            bg-white border-r border-gray-200
            transform transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0
            ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
            w-64 h-screen lg:h-screen
          `}
        >
          <div className="h-full flex flex-col">
            {/* Brand / Collapse */}
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">R</span>
                </div>
                {!sidebarCollapsed && (
                  <div>
                    <h1 className="text-lg font-bold text-gray-900">RoadServe</h1>
                    <p className="text-xs text-gray-500">Admin Portal</p>
                  </div>
                )}
              </div>
              <button
                onClick={() => setSidebarCollapsed((v) => !v)}
                className="hidden lg:inline-flex p-2 rounded-lg hover:bg-gray-100"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
              </button>
            </div>
          </div>

            {/* Nav (scrollable) */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    title={sidebarCollapsed ? item.label : undefined}
                    aria-current={active ? 'page' : undefined}
                    className={`
                      group relative flex items-center
                      ${sidebarCollapsed ? 'justify-center px-3' : 'space-x-3 px-4'}
                      py-2.5 rounded-lg transition
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                      ${active
                        ? 'bg-primary-50 text-primary-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    {/* Active indicator */}
                    <span
                      className={`
                        absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r
                        ${active ? 'bg-primary-600' : 'bg-transparent group-hover:bg-gray-200'}
                      `}
                    />
                    <span
                      className={`
                        inline-flex items-center justify-center
                        w-9 h-9 rounded-lg
                        ${active ? 'bg-white' : 'bg-transparent group-hover:bg-white'}
                        transition
                      `}
                    >
                      <Icon size={20} />
                    </span>
                    {!sidebarCollapsed && (
                      <span className="text-sm">
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Footer (always visible, not overlapping) */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} mb-3 ${sidebarCollapsed ? '' : 'px-2'}`}>
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-700 font-semibold text-sm">
                    {user?.email?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                {!sidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.email || 'Admin'}
                    </p>
                    <p className="text-xs text-gray-500">Administrator</p>
                  </div>
                )}
              </div>
              <button
                onClick={onLogout}
                className={`
                  w-full flex items-center
                  ${sidebarCollapsed ? 'justify-center' : 'space-x-3 px-2'}
                  py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
                `}
                title="Logout"
              >
                <LogOut size={20} />
                {!sidebarCollapsed && <span className="text-sm font-medium">Logout</span>}
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-0">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;

